// Secrets are write-only: the API never returns them, only whether they are
// set (has* flags). They can still be sent in create/update inputs.
export const PORTAL_DISABLED_MESSAGE_MAX_LENGTH = 500;

export const DEFAULT_PORTAL_DISABLED_MESSAGE =
  'Our returns portal is temporarily unavailable. Please check back soon, or contact our support team for help with your return.';

export type AdminRetailer = {
  _id: string;
  name: string;
  subdomain: string;
  headlessApiClientId: string;
  hasHeadlessApiClientSecret?: boolean;
  exchangesCheckbox: boolean;
  storeCreditReturnsCheckbox: boolean;
  calculateStoreCreditsEndpoint: string;
  availableItemsToExchangeEndpoint: string;
  createExchangeToCreditsEndpoint: string;
  createExchangeToItemEndpoint: string;
  exchangesAuthorizationType: string;
  hasExchangesXApiKey?: boolean;
  exchangesOAuthEndpoint: string;
  exchangesOAuthClientId: string;
  hasExchangesOAuthClientSecret?: boolean;
  exchangesOAuthScopes: string[];
  supportEmail: string;
  storeLocatorUrl: string;
  storeLocatorApiUrl?: string;
  returnPolicyDays: number;
  showLastCarrier: boolean;
  theme: {
    name: string;
    colorScheme: string;
    logo: string;
    favicon: string;
    metadata: Record<string, string>;
  };
  advertisingBanner: {
    banner: string;
    mobileBanner: string;
    tabletBanner: string;
    url: string;
  };
  informationCards: AdminInformationCard[];
  languages: string[];
  personalInfoStepDisabled: boolean;
  portalDisabled?: boolean;
  portalDisabledMessage?: string;
  nestedReturnReasonEnabled: boolean;
  randomizeReturnReasons: boolean;
  nestedReturnReasons: NestedReturnReasonOption[];
  createdAt: Date;
  updatedAt: Date;
  returnOptions: ReturnOption[];
  portal: PortalConfig;
  confirmationCheckBox: boolean;
  confirmationRadio: string;
  vouchersCheckBox: boolean;
  userJourneyRadio: string;
  programTypeRadio: string;
  termsAndConditionsCheckBox: boolean;
  termsAndConditionsUrl: string;
  confirmationTextTranslations: { [k: string]: string };
  faultyItem: string;
  returnPolicy: string;
  support: string;
};

export type PortalConfig = {
  name: string;
  enabledFeatures: CycleonRetailerFeaturesEnum[];
  expirationPolicy: {
    maxAge: number;
  };
};

export enum CycleonRetailerFeaturesEnum {
  TRACKING = 'TRACKING',
  PAYMENT = 'PAYMENT',
  DEDUCT_FROM_REFUND = 'DEDUCT_FROM_REFUND',
  GET_ORDER_DATA_FROM_CLIENT = 'GET_ORDER_DATA_FROM_CLIENT',
  DROP_OFF_MAP = 'DROP_OFF_MAP',
  BASE64_LABEL_IN_RESPONSE = 'BASE64_LABEL_IN_RESPONSE',
  ADAPTER_INTEGRATION = 'ADAPTER_INTEGRATION',
  REFUND_NOTIFICATION = 'REFUND_NOTIFICATION',
  CANCEL_RETURN = 'CANCEL_RETURN',
}

export enum UserJourneyEnum {
  EMAIL_VERIFICATION = 'email-verification',
  ORDER_LOOKUP = 'order-lookup',
  RETURN_WITHOUT_SALES_ORDER = 'return-without-sales-order',
}

export enum ReturnWithoutSalesOrderTypeEnum {
  TAKE_BACK = 'TAKE_BACK',
  RECYCLING = 'RECYCLING',
  DONATION = 'DONATION',
}

export enum RetailerAction {
  Refund = 'REFUND',
  ExchangeToCredits = 'STORE_CREDIT',
  ExchangeToItem = 'EXCHANGE',
}

export type ReturnOption = {
  _id: string;
  name: string;
  description: string;
  action: string;
  isFree: boolean;
};

export type RetailerFormValues = {
  name: string;
  subdomain: string;
  headlessApiClientId: string;
  // Secret inputs start empty; an empty value keeps the stored secret.
  headlessApiClientSecret: string;
  hasHeadlessApiClientSecret?: boolean;
  exchangesCheckbox: boolean;
  storeCreditReturnsCheckbox: boolean;
  calculateStoreCreditsEndpoint: string;
  availableItemsToExchangeEndpoint: string;
  createExchangeToCreditsEndpoint: string;
  createExchangeToItemEndpoint: string;
  exchangesAuthorizationType: string;
  exchangesXApiKey: string;
  hasExchangesXApiKey?: boolean;
  exchangesOAuthEndpoint: string;
  exchangesOAuthClientId: string;
  exchangesOAuthClientSecret: string;
  hasExchangesOAuthClientSecret?: boolean;
  exchangesOAuthScopes: string[];
  supportEmail: string;
  storeLocatorUrl: string;
  storeLocatorApiUrl?: string;
  returnPolicyDays: number;
  showLastCarrier: boolean;
  themeName: string;
  themeColorScheme: string;
  themeLogo: string;
  themeFavicon: string;
  landingImageTopLeft: string;
  landingImageBottomLeft: string;
  landingImageTopRight: string;
  landingImageBottomRight: string;
  landingImageTabletLeft: string;
  landingImageTabletTopRight: string;
  landingImageTabletBottomRight: string;
  advertisingBanner: string;
  advertisingBannerMobile: string;
  advertisingBannerTablet: string;
  advertisingBannerUrl: string;
  informationCardsNumber: number;
  informationCards: AdminInformationCard[];
  languages: string[];
  personalInfoStepDisabled: boolean;
  portalDisabled?: boolean;
  portalDisabledMessage?: string;
  nestedReturnReasonEnabled: boolean;
  randomizeReturnReasons: boolean;
  nestedReturnReasons: NestedReturnReasonFormValues[];
  returnOptions: ReturnOption[];
  confirmationCheckBox: boolean;
  confirmationRadio: string;
  vouchersCheckBox: boolean;
  userJourneyRadio: string;
  programTypeRadio: string;
  termsAndConditionsCheckBox: boolean;
  termsAndConditionsUrl: string;
  confirmationTextTranslations: { [k: string]: string };
  faultyItem: string;
  returnPolicy: string;
  support: string;
};

export type NestedReturnReasonFormValues = {
  returnReasonCode?: string;
  reasonCategoryPrettyName: { [k: string]: string };
  subReasons: SubReasonFormValues[];
};

export type SubReasonFormValues = {
  code?: string;
  prettyName: { [k: string]: string };
  hasComment: boolean;
  isCommentRequired: boolean;
  commentPlaceholder: { [k: string]: string };
};

export type NestedReturnReasonOption = {
  returnReasonCode: string;
  reasonCategoryPrettyName: TranslationOption[] | { [k: string]: string };
  subReasons: SubReasonOption[];
};

export type SubReasonOption = {
  code: string;
  prettyName: TranslationOption[] | { [k: string]: string };
  hasComment: boolean;
  isCommentRequired: boolean;
  commentPlaceholder: TranslationOption[] | { [k: string]: string };
};

export type TranslationOption = {
  key: string;
  value: string;
};

export type AdminInformationCard = {
  barColor: string;
  headlineTranslations: { [k: string]: string };
  contentTranslations: { [k: string]: string };
  linkTextTranslations: { [k: string]: string };
  linkUrl: string;
};

export enum ThemeColorScheme {
  Indigo = 'indigo',
  Rose = 'rose',
  Blue = 'blue',
  Orange = 'orange',
  Green = 'green',
  Neutral = 'neutral',
  Slate = 'slate',
  Red = 'red',
  Purple = 'purple',
}

export enum InformationCardBarColor {
  Disabled = 'disabled',
  Indigo = 'indigo',
  Yellow = 'yellow',
  Orange = 'orange',
  Green = 'green',
  Red = 'red',
}

export enum ThemeName {
  Impresionante = 'Impresionante',
}

export type Retailer = Pick<
  AdminRetailer,
  Exclude<
    keyof AdminRetailer,
    'headlessApiClientId' | 'hasHeadlessApiClientSecret'
  >
> & {};

export enum DeviceType {
  Desktop = 'Desktop',
  Tablet = 'Tablet',
  Mobile = 'Mobile',
}

export enum DesignSolutions {
  Activewear = 'Activewear',
}

export enum AuthorizationType {
  X_API_KEY = 'X_API_KEY',
  OAUTH_2_0 = 'OAUTH_2_0',
}

export type ReasonCodeOption = {
  key: string;
  value: string;
};

export type ReturnReasonModalFormValues = {
  reasonCode?: ReasonCodeOption;
  prettyName: { [k: string]: string };
  hasComment: boolean;
  isCommentRequired: boolean;
  commentPlaceholder: { [k: string]: string };
};

export enum Mode {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export enum EntityType {
  REASON = 'REASON',
  SUB_REASON = 'SUB_REASON',
}

export type ModalConfig = {
  visible: boolean;
  mode: Mode;
  entityType: EntityType;
  initialData?: ReturnReasonModalFormValues | undefined;
  parentId?: number;
  index?: number;
  reasonCodes?: ReasonCodeOption[];
};
